import { css } from 'styled-components';
import { opacify, focusDarken, px2rem } from '../utils/helpers/style.js';
import { TinyColor } from '@ctrl/tinycolor';
import { generateColorLuminances } from '../utils/helpers/generateColorLuminances.js';

/**
 * Blurs button after certain mouse events to avoid lingering focus styles
 */
var handleButtonUnfocusMouseEvent = function handleButtonUnfocusMouseEvent(onMouseEvent) {
  return function (e) {
    e.stopPropagation();
    e.currentTarget.blur();
    if (onMouseEvent) onMouseEvent(e);
  };
};
var getPlainFocusBg = function getPlainFocusBg(color) {
  var mode = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'lighten';
  return mode === 'lighten' ? opacify(color, 0.05) : new TinyColor(color).darken(focusDarken).toRgbString();
};
var getPlainActiveBg = function getPlainActiveBg(color) {
  var mode = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'lighten';
  return mode === 'lighten' ? opacify(color, 0.1) : new TinyColor(color).darken(focusDarken).toRgbString();
};
var getPlainButtonStyles = function getPlainButtonStyles(kind) {
  return function (props) {
    if (props.$invert) {
      var luminances = generateColorLuminances(kind, props.theme, {
        color: props.$color
      });
      return css(["background-color:", ";color:", ";"], luminances.bg, luminances.fg);
    }
    if (kind === 'hover' || kind === 'focus') {
      return css(["background-color:", ";color:", ";"], getPlainFocusBg(props.$color), props.$color);
    }
    if (kind === 'click') {
      return css(["background-color:", ";color:", ";"], getPlainActiveBg(props.$color), props.$color);
    }
    return css(["background-color:transparent;color:", ";"], props.$color);
  };
};
var getCampaignButtonStyles = function getCampaignButtonStyles(kind) {
  return function (props) {
    var _props$theme$button$w = props.theme.button.web.campaign,
      backgroundHover = _props$theme$button$w.backgroundHover,
      textColor = _props$theme$button$w.textColor,
      disabledBackground = _props$theme$button$w.disabledBackground,
      backgroundActive = _props$theme$button$w.backgroundActive,
      backgroundColor = _props$theme$button$w.backgroundColor;
    if (kind === 'hover' || kind === 'focus') {
      return css(["background-color:", ";color:", ";"], backgroundHover, textColor);
    }
    if (kind === 'disabled') {
      return css(["background-color:", ";color:", ";"], disabledBackground, textColor);
    }
    if (kind === 'click') {
      return css(["background-color:", ";color:", ";"], backgroundActive, textColor);
    }
    return css(["background-color:", ";color:", ";"], backgroundColor, textColor);
  };
};
var getTransparentButtonStyles =
// istanbul ignore next

function getTransparentButtonStyles() {
  var kind = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'normal';
  return function (props) {
    var backgroundColor = props.$status === 'primary' ? props.theme.tokens.button.desaturated : 'transparent';
    var borderStyles = props.$status === 'secondary' ? css(["border:solid ", ";border-color:", ";"], px2rem(props.theme.button.web.borders.width), props.theme.tokens.button.desaturated) : undefined;
    var textColor = props.$status === 'primary' ? props.$color : props.theme.tokens.button.desaturated;
    if (kind === 'hover' || kind === 'focus') {
      return css(["background-color:", ";color:", ";", ""], opacify(props.theme.tokens.button.desaturated, props.$status === 'primary' ? 0.9 : 0.2), textColor, borderStyles);
    }
    if (kind === 'click') {
      return css(["background-color:", ";color:", ";", ""], opacify(props.theme.tokens.button.desaturated, props.$status === 'primary' ? 0.8 : 0.4), textColor, borderStyles);
    }
    if (kind === 'disabled') {
      return css(["background-color:", ";color:", ";", ""], opacify(props.theme.tokens.button.desaturated, 0.2), opacify(props.theme.tokens.button.desaturated, 0.4), borderStyles);
    }
    return css(["background-color:", ";color:", ";", ""], backgroundColor, textColor, borderStyles);
  };
};
var getButtonStyles = function getButtonStyles() {
  var kind = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'normal';
  return function (props) {
    if (props.$transparent) {
      return getTransparentButtonStyles(kind)(props);
    }
    if (props.$status === 'campaign') {
      return getCampaignButtonStyles(kind)(props);
    }
    if (props.$status === 'plain' && kind !== 'disabled') {
      return getPlainButtonStyles(kind)(props);
    }
    var luminances = generateColorLuminances(kind, props.theme, {
      color: props.$color
    });

    // Secondary is an "inverted" style, when it comes to background and text

    var invert = props.$status === 'secondary' !== props.$invert && kind === 'normal';
    var fg = invert ? luminances.bg : luminances.fg;
    var bg = invert ? luminances.fg : luminances.bg;

    // Border is only visible on normal and secondary
    var border = kind === 'normal' && props.$status === 'secondary';

    // Background styles apply for all except non-inverted plain buttons
    var backgroundStyles = /*#__PURE__*/css(["background-color:", ";border:", " solid ", ";"], bg, border ? fg : bg, px2rem(props.theme.button.web.borders.width));
    return css(["color:", ";", ";"], props.$loading ? props.theme.colors.text : fg, backgroundStyles);
  };
};

export { getButtonStyles, getPlainActiveBg, getPlainFocusBg, handleButtonUnfocusMouseEvent };
